import { dataService } from '@app/modules/data';
import { DataObject, ObjectOptions } from './base';

import { TicketChange } from "./ticketchange";

import { AppConstants } from "@app/app.constants";
import { AccountInvoice } from './accountinvoice';

/*
    How does VeriFactu integration works
    There is a database table: VERIFACTU that is updated in the server
    - Relation with the ticket
    - Contents of the verifactu-qr
    - Errorcode returned by the verifactu server
    - CSV code returned by the verifactu server

    The ticket has a verifactu() property that returns an instance of this class
    The XML is sent in the verifactu commit operation
    - It is build and sent on the server, then the CSV returned code is kept 

    Offline considerations:
        The data sent to the server is stored in the syncchanges variable
*/

/*******************************/
/* VERIFACTU DATA CLASS        */
/*******************************/

export interface _VeriFactu {
    change: number,
    invceac: number,
    qr: string,
    xmldata?: string,
    xmlfile?: string,    
    sign: string,
    error: string,
    valid: string
};

interface _VeriFactuData extends _VeriFactu {
    objid?: number;
    _uuid?: string;
    created?: Date;
    updated?: Date;
};

abstract class VeriFactuData extends DataObject {
    protected _verifactu: _VeriFactuData = {
        change: null,
        invceac: null,
        qr: null,
        xmldata: null,
        xmlfile: null,
        sign: null,
        error: null,
        valid: null
    };

    constructor(table: string, objid: string, data: dataService, objoptions: ObjectOptions){
        super(table, objid, data, objoptions);

        this._verifactu.created = new Date();
        this._verifactu.updated = new Date();
    }

    /****************************/
    /* CLASS MEMBERS            */
    /****************************/

    get created(){
        return this._verifactu.created;
    }

    get updated(){
        return this._verifactu.updated;        
    }

    get change(): TicketChange {
        return this._children['change'] || null;
    }

    set change(value: TicketChange){
        if (this.SetChild('change', value, 'change')){
            this.ToUpdate = true;
        }
    }

    get invceac(): AccountInvoice {
        return this._children['invceac'] || null;
    }

    set invceac(value: AccountInvoice){
        if (this.SetChild('invceac', value, 'invceac')){
            this.ToUpdate = true;
        }
    }

    /* read only properties */

    get qr(){
        return this._verifactu.qr;
    }

    get xmldata(){
        return this._verifactu.xmldata;
    }

    get xmlfile(){
        return this._verifactu.xmlfile;
    }

    get sign(){
        return this._verifactu.sign;
    }

    get error(){
        return this._verifactu.error;
    }

    get valid(){
        return this._verifactu.valid;
    }

    /****************************/
    /* COMMIT OPERATION         */
    /****************************/

    protected get Change() {
        return {
            tckchg: this._verifactu.change,
            invceac: this._verifactu.invceac,
            qr: this._verifactu.qr,
            xml: this._verifactu.xmldata,    // this is the xml content sent to the server
            sign: this._verifactu.sign            
        };
    }

    protected get Depend() {
        return {
            tckchg: { item: this.change, relation_info: { to: 'vetifactu', by: 'change' } },     // this[by -> 'change'][to -> 'vetifactu'] => this
            invceac: { item: this.invceac, relation_info: { to: 'vetifactu', by: 'invceac' } },  // this[by -> 'invceac'][to -> 'vetifactu'] => this
        };
    }

    protected get Children(){
        return [];
    }

    /****************************/
    /* DATA OBJECT              */
    /****************************/
    
    private _patchData(_verifactu: _VeriFactu){
        let _toUpdate = false;

        _toUpdate = this.patchValue(this._verifactu, 'change', _verifactu['change']) || _toUpdate;
        _toUpdate = this.patchValue(this._verifactu, 'invceac', _verifactu['invceac']) || _toUpdate;
        _toUpdate = this.patchValue(this._verifactu, 'qr', _verifactu['qr']) || _toUpdate;
        _toUpdate = this.patchValue(this._verifactu, 'xmlfile', _verifactu['xmlfile']) || _toUpdate;
        _toUpdate = this.patchValue(this._verifactu, 'sign', _verifactu['sign']) || _toUpdate;
        _toUpdate = this.patchValue(this._verifactu, 'error', _verifactu['error']) || _toUpdate;
        _toUpdate = this.patchValue(this._verifactu, 'valid', _verifactu['valid']) || _toUpdate;
    
        return false;   // these values are managed by the server and should not trigger an update by themselves
    }   

    set Data(_verifactu: _VeriFactu){
        this.patchValue(this._verifactu, 'created', _verifactu['created']);
        this.patchValue(this._verifactu, 'updated', _verifactu['updated']);
        
        if (this._patchData(_verifactu)){
            this.ToUpdate = true;
        }
    }

    get Info(){
        return this._verifactu;
    }

    set Info(value){
        this.DoPatchValues(value);
    }
   
    private DoPatchValues(_verifactu: _VeriFactu){
        this._patchData(_verifactu);

        if (_verifactu['change']){    // update children: 'change'
            let _objid = _verifactu['change'].toString();
            this.SetChild('change', new TicketChange(_objid, this.data, this._objoptions), 'change')
        }
        else {
            this.SetChild('change', null, 'change');
        }
    }

    private _ddbb(info): _VeriFactuData {
        let _verifactu: _VeriFactuData = {
            objid: info['objid'] ? parseInt(info['objid']) : null,
            created: new Date(Date.parse(this.mysqlToDateStr(info['created']))),
            updated: new Date(Date.parse(this.mysqlToDateStr(info['updated']))),
            change: info['tckchg'] ? parseInt(info['tckchg']) : null,
            invceac: info['invceac'] ? parseInt(info['invceac']) : null,
            qr: info['qr'],
            xmlfile: info['xml'],   // this is the xmlfile returned from the server
            sign: info['sign'],
            error: info['error'],
            valid: info['valid'],
        };
        return _verifactu;
    }

    protected _OnUpdate(info){
        let _verifactu = this._ddbb(info);
        this.patchValue(this._verifactu, 'objid', _verifactu['objid']);
        this.patchValue(this._verifactu, 'created', _verifactu['created']);
        this.patchValue(this._verifactu, 'updated', _verifactu['updated']);
        this.DoPatchValues(_verifactu);
    }
}

export class VeriFactu extends VeriFactuData {
    constructor(objid: string, data: dataService, objoptions: ObjectOptions = null){
        super('VERIFACTU', objid, data, objoptions);
    }

    Copy(store: Array<DataObject> = []): VeriFactu {
        return this._Copy(store) as VeriFactu;
    }

    /****************************/
    /* CUSTOM MEMBERS           */
    /****************************/

    private _verifactucalc = null;
    private get VFacCalc(){
        if (this._verifactucalc == null){
            this._verifactucalc = new VeriFactuCalc(this.change, this.invceac, this.data);
        }
        return this._verifactucalc;
    }

    get IsAvailable(){
        return (this.VFacCalc.IsAvailable);
    }

    async Refresh(){
        if (this.change){
            // do not calculate if no invceno
            if (!this.change.invoice){
                return {
                    'qr': null
                }              
            }

            if (this.change.ToInsert || this.change.IsLoaded){
                if (this.change.reason == 'C'){     // this is a cancellation
                    // recalculate the xml
                    this._verifactu.xmldata = await this.VFacCalc.xml();
                    this._verifactu.sign = await this.VFacCalc.sign();
                    
                    if ((this._verifactu.xmldata == null) || (this._verifactu.sign == null)){
                        return null;    // error obtaining ticketbai information
                    }

                    // cancelled tickets do not provide qr
                    this._verifactu.qr = null;
                }
                else {
                    this._toUpdate  = (!this._verifactu.qr || this.change.ToUpdate);

                    if(this._toUpdate ) { 
                        console.info("[VERIFACTU] Generating QR code (change reason '" + this.change.reason+ "')");

                        // recalculate the xml
                        this._verifactu.xmldata = await this.VFacCalc.xml();
                        this._verifactu.sign = await this.VFacCalc.sign();
                        
                        // recalculate qr
                        this._verifactu.qr = await this.VFacCalc.qr();        
                    }                    
                }

                return {
                    'qr': this._verifactu.qr
                }  
            }
        }

        if (this.invceac){
            // do not calculate if no invceno
            if (!this.invceac.invoice){
                return {
                    'qr': null
                }              
            }

            this._toUpdate  = (!this._verifactu.qr);

            if(this._toUpdate ) { 
                console.info("[VERIFACTU] Generating QR code (change reason 'A')");

                // recalculate the xml
                this._verifactu.xmldata = await this.VFacCalc.xml();
                this._verifactu.sign = await this.VFacCalc.sign();

                if ((this._verifactu.xmldata == null) || (this._verifactu.sign == null)){
                    return null;    // error obtaining ticketbai information
                }

                // recalculate  qr
                this._verifactu.qr = await this.VFacCalc.qr();                       
            }

            return {
                'qr': this._verifactu.qr
            }  
        }

        return null;    // ticket is not ready (not created and not loaded)
    }

    /****************************/
    /* CUSTOM METHODS           */
    /****************************/

    get IsValid(){
        return (this.valid != null);
    }
}

/*******************************/
/* VERIFACTU LOGIC CLASS       */
/*******************************/

class VeriFactuCalc {
    private _verifactu_qr = null;

    constructor(private _change: TicketChange, private _invceac: AccountInvoice, private _data: dataService){
        // nothing to do
    }

    get place(){
        if (this._change){
            return this._change.ticket.place;
        }

        if (this._invceac){
            return this._invceac.account.place;
        }

        return null;
    }
    
    get client(){
        if (this._change){
            return this._change.client;
        }

        if (this._invceac){
            return this._invceac.account.client;
        }

        return null;
    }

    private _verifactu_url(){
        const _sbox = 'https://prewww2.aeat.es/wlpl/TIKE-CONT/ValidarQR';
        const _prod = 'https://www2.agenciatributaria.gob.es/wlpl/TIKE-CONT/ValidarQR';

        return AppConstants.VeriFactuSandbox ? _sbox : _prod;
    }

    private _verifactu_series(){
        if (this._change){
            return this._change.series;            
        }

        if (this._invceac){
            return this._invceac.series;
        }

        return null;
    }

    private _verifactu_invoice(){
        if (this._change){
            return this._change.invoice;            
        }

        if (this._invceac){
            return this._invceac.invoice;
        }

        return null;
    }

    private _verifactu_amount(){
        if (this._change){
            let _amount = this._change.total;
            if ((this._change.reason == 'P') && (this._change.prev)){
                _amount = _amount - this._change.prev.total;
            }

            return _amount.toFixed(2);
        }

        if (this._invceac){
            return this._invceac.total.toFixed(2);
        }

        return null;
    }

    private _verifactu_fecha(){
        let _date = null;

        if (this._change){
            _date = this._change.created;
        }

        if (this._invceac){
            _date = this._invceac.created;
        }

        if (_date){
            const dd = _date.getDate().toString().padStart(2, '0');
            const mm = (_date.getMonth() + 1).toString().padStart(2, '0');
            const aaaa = _date.getFullYear();
    
            return dd + '-' + mm + '-' + aaaa;    
        }

        return null;
    }

    /*
        id: Código identificativo
        - Url del servicio verifactu de consulta de códigos QR.
        - ?nif: Nif del emisor de la factura
        - &numserie: Número de serie + número de factura
        - &fecha: Fecha de expedición de la factura en formato DD-MM-AAAA
        - &importe: Importe de la factura
    */    

    async qr(){
        return new Promise(async (resolve) => {
            if (this._verifactu_qr == null){
                let _qr = "";

                _qr = this._verifactu_url();
                _qr += '?nif=' + this.place.taxid;
                _qr += '&numserie=' + this._verifactu_series() + "/" + this._verifactu_invoice();
                _qr += '&fecha=' + this._verifactu_fecha();
                _qr += '&importe=' + this._verifactu_amount();

                this._verifactu_qr = _qr    
            }

            resolve(this._verifactu_qr);
        });
    }

    async xml(){
        return await this._verifactu_xml();
    }    

    async sign(){
        return await this._verifactu_sign();
    }

    /*********************************/
    /* PRIVATE METHODS               */
    /*********************************/

    private _verifactu_xml_date(date){
        const dd = String(date.getDate()).padStart(2, '0');
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const yy = date.getFullYear();

        return `${dd}-${mm}-${yy}`;  // DD-MM-YYYY
    }

    private _verifactu_data = null;
    private async _verifactu_xml(){
        if (!this._verifactu_data){
            this._verifactu_data = await this._buildXML();
        }
        return this._verifactu_data;
    }
    
    private _verifactu_huella = null;
    private async _verifactu_sign(){
        if (!this._verifactu_data){
            this._verifactu_data = await this._buildXML();
        }
        return this._verifactu_huella;  // calculated within the xml
    }

    private async _buildXML(){
        if (this._data.view.Access == 'GUEST'){
            return null;    // will be calculated on server side
        }  

        let _reason = (this._change) ? this._change.reason : 'A';
        if (_reason == 'C'){
            let _XMLData = await this.XMLData('BAJA');
            if (!_XMLData){
                return null;
            }

            return this._UnsignedXML_Baja(_XMLData);
        }
        else {
            let _XMLData = await this.XMLData('ALTA');
            if (!_XMLData){
                return null;
            }

            return this._UnsignedXML_Alta(_XMLData);
        }    
    }

    private async verifactu_DatosImportes(xmldata){
        let _taxsplit = {};

        let _totalcharge = 0;
        let _totaldiscount = 0;

        if (this._change){
            let _products = [];
            _products = _products.concat(this._change.ticket.ProductsToPrepare);
            _products = _products.concat(this._change.ticket.ProductsDelivered);

            let _requested = [];
            for(let _item of _products){
                if (_item.product.product == null){
                    continue;   // do not notify ticket separators
                }

                _requested = _requested.concat(_item.product.requested);
            }

            for(let requested of _requested){
                let _taxrate = Number(requested.taxrate) || AppConstants.defaultTaxRate;;
                let _charged = Number(requested.ChargePrice);

                if (!(_taxrate in _taxsplit)){
                    _taxsplit[_taxrate] = 0;
                }
                _taxsplit[_taxrate] += _charged;
                _totalcharge += _charged;
            }

            let _extras = [];
            _extras = _extras.concat(this._change.ticket.ExtrasToPrepare);
            for(let _item of _extras){
                let _taxrate = _item.taxrate || AppConstants.defaultTaxRate;
                let _charged = Number(_item.ChargePrice);

                if (!(_taxrate in _taxsplit)){
                    _taxsplit[_taxrate] = 0;
                }
                _taxsplit[_taxrate] += _charged;
                _totalcharge += _charged;
            }

            // calculate the total discount
            let _discounts = [];
            _discounts = _discounts.concat(this._change.ticket.DiscountsToApply);        
            for(let _item of _discounts){
                _totaldiscount += Number(_item.ChargePrice);
            }

            // apply proportionally the discount
            let disctpcnt = _totalcharge > 0 ? (_totalcharge - _totaldiscount) / _totalcharge : 1;
            for (let _rate in _taxsplit){
                _taxsplit[_rate] = _taxsplit[_rate] * disctpcnt;
            }
        }

        if (this._invceac){
            let _taxrates = this._invceac.SplitTaxes;
            for (let _taxrate of _taxrates){
                _taxsplit[_taxrate['rate']] = _taxrate['total'];
            }            
        }

        let _desglose = [];
        for (let _rate in _taxsplit){
            let _base = Math.round((_taxsplit[_rate] / (1 + (Number(_rate)/100))) * 100)/100;
            let _taxes = Math.round((_taxsplit[_rate] - _base) * 100)/100;

            _desglose.push({
                ClaveRegimen: '01',              // Operación de régimen general.
                CalificacionOperacion: 'S1',     //  No exenta - Sin inversion sujeto pasivo
                TipoImpositivo: Number(_rate),
                BaseImponible: Number(_base),
                CuotaRepercutida: Number(_taxes)
            });
        }

        let _cuotaTotal = 0;
        let _importeTotal = 0;

        for(let _rate of _desglose){
            _importeTotal += _rate['CuotaRepercutida'] + _rate['BaseImponible'];
            _cuotaTotal += _rate['CuotaRepercutida'];
        }

        return {
            DetalleDesglose: _desglose,
            CuotaTotal: Math.round(_cuotaTotal * 100)/100,
            ImporteTotal: Math.round(_importeTotal * 100)/100
        }
    }

    private async _sha256UpperHex(input) {
        const encoder = new TextEncoder();
        const data = encoder.encode(input);

        const hashBuffer = await crypto.subtle.digest('SHA-256', data); // binary hash
        const hashArray = Array.from(new Uint8Array(hashBuffer));       // array of bytes
        const hexString = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

        return hexString.toUpperCase();
    }

    // https://www.agenciatributaria.es/static_files/AEAT_Desarrolladores/EEDD/IVA/VERI-FACTU/Veri-Factu_especificaciones_huella_hash_registros.pdf
    private async verifactu_huellaFactura(xmldata, operation: 'ALTA' | 'BAJA'){
        let _previous = xmldata.previoussign;

        let _calstr = '';
        switch(operation){
            case 'ALTA':
                _calstr += "IDEmisorFactura=" + xmldata['ticketdata']['IdEmisorFactura'] + "&"
                _calstr += "NumSerieFactura=" + xmldata['ticketdata']['NumSerieFactura'] + "&";             
                _calstr += "FechaExpedicionFactura=" + xmldata['ticketdata']['FechaExpedicionFactura'] + "&";        
                _calstr += "TipoFactura=" + xmldata['ticketdata']['TipoFactura'] + "&";        
                _calstr += "CuotaTotal=" + xmldata['ticketdata']['CuotaTotal'] + "&";        
                _calstr += "ImporteTotal=" + xmldata['ticketdata']['ImporteTotal'] + "&";        
                _calstr += "Huella=" + (_previous == null ? '' : _previous['Huella']) + "&";    
                _calstr += "FechaHoraHusoGenRegistro=" + xmldata['ticketdata']['FechaGenRegistro'];
                break;

            case 'BAJA':
                _calstr += "IDEmisorFacturaAnulada="+ xmldata['ticketcancel']['IdEmisorFactura'] + "&";
                _calstr += "NumSerieFacturaAnulada="+ xmldata['ticketcancel']['NumSerieFactura'] + "&";
                _calstr += "FechaExpedicionFacturaAnulada="+ xmldata['ticketcancel']['FechaExpedicionFactura'] + "&";
                _calstr += "Huella=" + (_previous == null ? '' : _previous['Huella']) + "&";
                _calstr += "FechaHoraHusoGenRegistro="+ xmldata['ticketdata']['FechaGenRegistro'];
                break;
        }

        return await this._sha256UpperHex(_calstr);
    }

    private async verifactu_DatosFactura(xmldata, operation: 'ALTA' | 'BAJA'){
        let nombreEmisorFactura = this.place.business;
        let idEmisorFactura = this.place.taxid;
        let destinatarioFactura = false;
        let nombreDestinatarioFactura = null;
        let idDestinatarioFactura = null;
        let numSerieFactura = null;
        let fechaExpedicionFactura = null;
        let tipoFactura = null;
        let tipoRectificativa = null;
        let descripcionFactura = null;

        let cuotaTotal = null;
        let importeTotal = null;

        if (xmldata['tickettaxes']){
            cuotaTotal = xmldata['tickettaxes']['CuotaTotal'];
            importeTotal = xmldata['tickettaxes']['ImporteTotal'];
        }

        let _client = this.client;
        if (_client){
            destinatarioFactura = true;
            nombreDestinatarioFactura = _client.name;
            idDestinatarioFactura = _client.taxid;
        }   
        
        if (this._change){
            numSerieFactura = this._change['series'] + '/' + this._change['invoice'],
            fechaExpedicionFactura = this._verifactu_xml_date(this._change.created);

            // factura completa no rectificativa
            if (this._change.reason == 'C'){    
                tipoFactura = 'F1';       // Factura (art. 6, 7.2 y 7.3 del RD 1619/2012)
                descripcionFactura = "Factura completa: " + this.place.name;
            }

            // factura simplificada no rectificativa
            if (this._change.reason == 'F'){
                tipoFactura = 'F2';        // Factura Simplificada y y Facturas sin identificación del destinatario art. 6.1.d) RD 1619/2012
                descripcionFactura = "Factura simplificada: " + this.place.name;
            }

            // factura completa y rectificativa    
            if (this._change.reason == 'I'){
                tipoFactura = 'F3';        // Factura emitida en sustitución de facturas simplificadas facturadas y declaradas                
                descripcionFactura = 'Emisión de factura';
            }        

            if (this._change.reason == 'D'){
                tipoFactura = 'R4';        // Factura Rectificativa (Resto) -- Cambio en destinatario
                tipoRectificativa = 'S';   // Por sustitucion                
                descripcionFactura = 'Cambios en destinatario';
            }

            if (this._change.reason == 'P'){
                if (destinatarioFactura){
                    tipoFactura = 'R1';        // Factura Rectificativa (Error fundado en derecho y Art. 80 Uno Dos y Seis LIVA)
                }   
                else {
                    tipoFactura = 'R5';        // Factura Rectificativa en facturas simplificadas
                }         

                tipoRectificativa = 'I';   // Por diferencias
                descripcionFactura = 'Cambios en importe';                            
            }
        }

        if (this._invceac) {
            numSerieFactura = this._invceac['series'] + '/' + this._invceac['invoice'],
            fechaExpedicionFactura = this._verifactu_xml_date(this._invceac.created);

            descripcionFactura = 'Factura recapitulativa';            
            tipoFactura = 'F3';        // Factura emitida en sustitución de facturas simplificadas facturadas y declaradas
        }

        let _ticketdata = {
            NombreEmisorFactura: nombreEmisorFactura,
            IdEmisorFactura: idEmisorFactura,
            DestinatarioFactura: destinatarioFactura,
            NombreDestinatarioFactura: nombreDestinatarioFactura,
            IdDestinatarioFactura: idDestinatarioFactura,
            NumSerieFactura: numSerieFactura,
            FechaExpedicionFactura: fechaExpedicionFactura,
            TipoFactura: tipoFactura,
            CuotaTotal: cuotaTotal,
            ImporteTotal: importeTotal,
            TipoRectificativa: tipoRectificativa,
            DescripcionFactura: descripcionFactura,
            FechaGenRegistro: new Date().toISOString()
        }

        // obtain the invoice signature 
        xmldata['ticketdata'] = _ticketdata;

        _ticketdata['HuellaFactura'] = await this.verifactu_huellaFactura(xmldata, operation);
        _ticketdata['TipoHuella'] = '01';   // base64 of sha-256

        this._verifactu_huella = _ticketdata['HuellaFactura'];

        return _ticketdata;
    }

    private async verifactu_DatosCancelada(xmldata){
        let idEmisorFactura = this.place.taxid;
        let numSerieFactura = null;
        let fechaExpedicionFactura = null;

        let _change = this._change;
        while (_change && (_change.reason != 'I') && _change.prev){
            _change = _change.prev;
        }

        if (_change){
            numSerieFactura = _change['series'] + '/' + _change['invoice'],
            fechaExpedicionFactura = this._verifactu_xml_date(_change.created);
        }

        let _ticketdata = {
            IdEmisorFactura: idEmisorFactura,
            NumSerieFactura: numSerieFactura,
            FechaExpedicionFactura: fechaExpedicionFactura,
        }

        return _ticketdata;
    }

    private async verifactu_DatosSustituida(xmldata){
        let ticketreplace = {}
        let replacements = [];

        let importeRectificado = Number(xmldata['ticketdata']['ImporteTotal']);
        let cuotaRectificada = Number(xmldata['ticketdata']['CuotaTotal'])
        let baseRectificada = importeRectificado - cuotaRectificada;

        if (this._change){
            /*
                Factura completa en sustitucion de simplificada
                El importe total, es el importe final
                En las facturas sustituidas incluir la original y las rectificaciones
            */

            if (xmldata['ticketdata']['TipoFactura'] == 'F3'){
                let _prev = this._change.prev;
                while(_prev){
                    replacements.push({
                        NumSerieFactura: _prev['series'] + "/" + _prev['invoice'],
                        FechaExpedicionFactura: this._verifactu_xml_date(_prev.created)
                    })

                    _prev = _prev.prev;     // get the source invoice
                }


                ticketreplace['FacturasSustituidas'] = replacements;
            }

            /*
                Cambios en el destinatario sobre factura completa
                El importe total, es el importe final
                La diferencia de importes sobre la factura sustituida será 0
            */

            if (xmldata['ticketdata']['TipoFactura'] == 'R4'){
                let _prev = this._change.prev;

                if (_prev) {
                    // BaseRectificada and CuotaRectificada must reflect original invoice amounts, not the difference
                    baseRectificada = Number(_prev.base);
                    cuotaRectificada = Number(_prev.taxes);

                    replacements.push({
                        NumSerieFactura: _prev['series'] + "/" + _prev['invoice'],
                        FechaExpedicionFactura: this._verifactu_xml_date(_prev.created)
                    })
                }

                ticketreplace['FacturasSustituidas'] = replacements;
                ticketreplace['ImporteRectificacion'] = {
                    BaseRectificada: Math.round(baseRectificada * 100)/100,
                    CuotaRectificada: Math.round(cuotaRectificada * 100)/100
                }
            }

            /* 
                Rectificativas por diferencias: Indicar la diferencia de importes
                Estos valores se indican en el 'importe total', 'cuota total' y en el 'desglose'
            */     

            if (xmldata['ticketdata']['TipoRectificativa'] == 'I'){
                let _prev = this._change.prev;
                if (_prev) {
                    // Calculate difference directly
                    let totalDiff = Number(this._change.total) - Number(_prev.total);
                    
                    // Calculate base and cuota directly from the difference
                    let _currentDesglose = xmldata['tickettaxes']['DetalleDesglose'];
                    
                    // Get tax rate from current desglose or use default (10% for hostelería)
                    let _taxRate = (_currentDesglose && _currentDesglose.length > 0) 
                        ? _currentDesglose[0]['TipoImpositivo'] 
                        : (AppConstants.defaultTaxRate || 10);
                    
                    let baseDiff = Math.round((totalDiff / (1 + _taxRate / 100)) * 100) / 100;
                    let cuotaDiff = Math.round((totalDiff - baseDiff) * 100) / 100;
                    
                    // Always create DetalleDesglose (Verifactu requires at least one, even with 0 values)
                    // Hardcoded for hostelería: ClaveRegimen '01' (régimen general), CalificacionOperacion 'S1' (operación sujeta)
                    let _diffDesglose = [{
                        ClaveRegimen: '01',
                        CalificacionOperacion: 'S1',
                        TipoImpositivo: _taxRate,
                        BaseImponible: baseDiff,
                        CuotaRepercutida: cuotaDiff
                    }];
                    
                    xmldata['tickettaxes']['DetalleDesglose'] = _diffDesglose;
                    xmldata['tickettaxes']['ImporteTotal'] = Math.round(totalDiff * 100)/100;
                    xmldata['tickettaxes']['CuotaTotal'] = cuotaDiff;
                    
                    // Also update ticketdata
                    xmldata['ticketdata']['ImporteTotal'] = Math.round(totalDiff * 100)/100;
                    xmldata['ticketdata']['CuotaTotal'] = cuotaDiff;

                    // Add FacturasRectificadas
                    replacements.push({
                        NumSerieFactura: _prev['series'] + "/" + _prev['invoice'],
                        FechaExpedicionFactura: this._verifactu_xml_date(_prev.created)
                    });
                    ticketreplace['FacturasSustituidas'] = replacements; // Will be used as FacturasRectificadas for R types

                    // Recalculate huella with updated values
                    xmldata['ticketdata']['HuellaFactura'] = await this.verifactu_huellaFactura(xmldata, 'ALTA');
                }
            }
        }

        if (this._invceac){
            let _tickets = this._invceac.tickets;
            
            for (let _ticket of _tickets){
                replacements.push({
                    NumSerieFactura: _ticket['series'] + "/" + _ticket['invoice'],
                    FechaExpedicionFactura: this._verifactu_xml_date(_ticket['created'])
                })
            }

            ticketreplace['FacturasSustituidas'] = replacements;
        }
        
        return ticketreplace;
    }

    private async verifactu_DatosEncademaniento(xmldata){
        let _previous = await this._data.GetLastInvoice();
        if (_previous){
            return {
                IDEmisorFactura: this.place.taxid,
                NumSerieFactura: _previous['series'] + '/' + _previous['invoice'],
                FechaExpedicionFactura: _previous['created'],
                Huella: _previous['vftu']
            }
        }
        else {
            console.warn("[VERIFACTU] This is the first invoice of the series!")
        }
        
        return null;
    }

    private verifactu_InstalationNumber(){
        return this._data.serial;   // unico por local y dispositivo
    }

    private async verifactu_DatosSoftware(xmldata){
        return {
            NombreRazon: AppConstants.VerifactuDeveloperName(),
            NIF: AppConstants.VerifactuDeveloperNIF(),
            NombreSistemaInformatico: AppConstants.VerifactuApplicationName(),
            IdSistemaInformatico: AppConstants.VerifactuApplicationId(),
            Version: AppConstants.VerifactuApplicationVers(),
            NumeroInstalacion: this.verifactu_InstalationNumber(),
            TipoUsoPosibleSoloVerifactu: 'N',
            TipoUsoPosibleMultiOT: 'S',
            IndicadorMultiplesOT: 'S'
        }
    }

    private async XMLData(operation: 'ALTA' | 'BAJA'){
        const _xmldata = {
            ticketdata: null,
            ticketreplace: null,
            ticketcancel: null,
            tickettaxes: null,
            previoussign: null,
            softwaredata: null
        }

        _xmldata.previoussign = await this.verifactu_DatosEncademaniento(_xmldata);
        _xmldata.tickettaxes = await this.verifactu_DatosImportes(_xmldata);
        _xmldata.ticketcancel = await this.verifactu_DatosCancelada(_xmldata);
        _xmldata.ticketdata = await this.verifactu_DatosFactura(_xmldata, operation);
        _xmldata.ticketreplace = await this.verifactu_DatosSustituida(_xmldata);
        _xmldata.softwaredata = await this.verifactu_DatosSoftware(_xmldata);

        return _xmldata;
    }

    private async _UnsignedXML_Baja(_XMLData){
        let _xml = '';

        _xml += '<sfLR:RegistroFactura>';
        _xml += '<sf:RegistroAnulacion>';
        _xml += '<sf:IDVersion>1.0</sf:IDVersion>';
        _xml += `
            <sf:IDFactura>
                <sf:IDEmisorFacturaAnulada>${_XMLData['ticketcancel']['IdEmisorFactura']}</sf:IDEmisorFacturaAnulada>
                <sf:NumSerieFacturaAnulada>${_XMLData['ticketcancel']['NumSerieFactura']}</sf:NumSerieFacturaAnulada>
                <sf:FechaExpedicionFacturaAnulada>${_XMLData['ticketcancel']['FechaExpedicionFactura']}</sf:FechaExpedicionFacturaAnulada>
            </sf:IDFactura>
        `;        

        _xml += '<sf:Encadenamiento>';
        if (_XMLData['previoussign']){
            _xml += `
                <sf:RegistroAnterior>
                <sf:IDEmisorFactura>${_XMLData['previoussign']['IDEmisorFactura']}</sf:IDEmisorFactura>
                <sf:NumSerieFactura>${_XMLData['previoussign']['NumSerieFactura']}</sf:NumSerieFactura>
                <sf:FechaExpedicionFactura>${_XMLData['previoussign']['FechaExpedicionFactura']}</sf:FechaExpedicionFactura>
                <sf:Huella>${_XMLData['previoussign']['Huella']}</sf:Huella>
                </sf:RegistroAnterior>
            `;
        }
        else {
            _xml += '<sf:PrimerRegistro>S</sf:PrimerRegistro>';
        }
        _xml += '</sf:Encadenamiento>';
        
        _xml += `
            <sf:SistemaInformatico>
                <sf:NombreRazon>${_XMLData['softwaredata']['NombreRazon']}</sf:NombreRazon>
                <sf:NIF>${_XMLData['softwaredata']['NIF']}</sf:NIF>
                <sf:NombreSistemaInformatico>${_XMLData['softwaredata']['NombreSistemaInformatico']}</sf:NombreSistemaInformatico>
                <sf:IdSistemaInformatico>${_XMLData['softwaredata']['IdSistemaInformatico']}</sf:IdSistemaInformatico>
                <sf:Version>${_XMLData['softwaredata']['Version']}</sf:Version>
                <sf:NumeroInstalacion>${_XMLData['softwaredata']['NumeroInstalacion']}</sf:NumeroInstalacion>
                <sf:TipoUsoPosibleSoloVerifactu>${_XMLData['softwaredata']['TipoUsoPosibleSoloVerifactu']}</sf:TipoUsoPosibleSoloVerifactu>
                <sf:TipoUsoPosibleMultiOT>${_XMLData['softwaredata']['TipoUsoPosibleMultiOT']}</sf:TipoUsoPosibleMultiOT>
                <sf:IndicadorMultiplesOT>${_XMLData['softwaredata']['IndicadorMultiplesOT']}</sf:IndicadorMultiplesOT>
            </sf:SistemaInformatico>
        `;

        _xml += `<sf:FechaHoraHusoGenRegistro>${_XMLData['ticketdata']['FechaGenRegistro']}</sf:FechaHoraHusoGenRegistro>`;
        _xml += `<sf:TipoHuella>${_XMLData['ticketdata']['TipoHuella']}</sf:TipoHuella>`;
        _xml += `<sf:Huella>${_XMLData['ticketdata']['HuellaFactura']}</sf:Huella>`;

        _xml += '</sf:RegistroAnulacion>';
        _xml += '</sfLR:RegistroFactura>';   

        return _xml;
    }

    private async _UnsignedXML_Alta(_XMLData){
        let _xml = '';

        _xml += '<sfLR:RegistroFactura>';
        _xml += '<sf:RegistroAlta>';
        _xml += '<sf:IDVersion>1.0</sf:IDVersion>';
        _xml += `
            <sf:IDFactura>
                <sf:IDEmisorFactura>${_XMLData['ticketdata']['IdEmisorFactura']}</sf:IDEmisorFactura>
                <sf:NumSerieFactura>${_XMLData['ticketdata']['NumSerieFactura']}</sf:NumSerieFactura>
                <sf:FechaExpedicionFactura>${_XMLData['ticketdata']['FechaExpedicionFactura']}</sf:FechaExpedicionFactura>
            </sf:IDFactura>
        `;
        _xml += `<sf:NombreRazonEmisor>${_XMLData['ticketdata']['NombreEmisorFactura']}</sf:NombreRazonEmisor>`;
        _xml += `<sf:TipoFactura>${_XMLData['ticketdata']['TipoFactura']}</sf:TipoFactura>`;
        if (_XMLData['ticketdata']['TipoRectificativa']){
            _xml += `<sf:TipoRectificativa>${_XMLData['ticketdata']['TipoRectificativa']}</sf:TipoRectificativa>`;
        }

        if (_XMLData['ticketreplace'] && _XMLData['ticketreplace']['FacturasSustituidas'] && (_XMLData['ticketreplace']['FacturasSustituidas'].length > 0)){
            if (_XMLData['ticketdata']['TipoFactura'].startsWith('F')){
                _xml += '<sf:FacturasSustituidas>';
                for(let _replace of _XMLData['ticketreplace']['FacturasSustituidas']){
                    _xml += `
                        <sf:IDFacturaSustituida>
                        <sf:IDEmisorFactura>${_XMLData['ticketdata']['IdEmisorFactura']}</sf:IDEmisorFactura>
                        <sf:NumSerieFactura>${_replace['NumSerieFactura']}</sf:NumSerieFactura>
                        <sf:FechaExpedicionFactura>${_replace['FechaExpedicionFactura']}</sf:FechaExpedicionFactura>
                        </sf:IDFacturaSustituida>
                    `;
                }
                _xml += '</sf:FacturasSustituidas>';

                if (_XMLData['ticketreplace']['ImporteRectificacion']){
                    _xml += `
                        <sf:ImporteRectificacion>
                            <sf:BaseRectificada>${_XMLData['ticketreplace']['ImporteRectificacion']['BaseRectificada']}</sf:BaseRectificada>
                            <sf:CuotaRectificada>${_XMLData['ticketreplace']['ImporteRectificacion']['CuotaRectificada']}</sf:CuotaRectificada>
                        </sf:ImporteRectificacion>
                    `;
                }
            }

            if (_XMLData['ticketdata']['TipoFactura'].startsWith('R')){
                _xml += '<sf:FacturasRectificadas>';
                for(let _replace of _XMLData['ticketreplace']['FacturasSustituidas']){
                    _xml += `
                        <sf:IDFacturaRectificada>
                            <sf:IDEmisorFactura>${_XMLData['ticketdata']['IdEmisorFactura']}</sf:IDEmisorFactura>
                            <sf:NumSerieFactura>${_replace['NumSerieFactura']}</sf:NumSerieFactura>
                            <sf:FechaExpedicionFactura>${_replace['FechaExpedicionFactura']}</sf:FechaExpedicionFactura>
                        </sf:IDFacturaRectificada>
                    `;
                }
                _xml += '</sf:FacturasRectificadas>';

                if (_XMLData['ticketreplace']['ImporteRectificacion']){
                    _xml += `
                        <sf:ImporteRectificacion>
                            <sf:BaseRectificada>${_XMLData['ticketreplace']['ImporteRectificacion']['BaseRectificada']}</sf:BaseRectificada>
                            <sf:CuotaRectificada>${_XMLData['ticketreplace']['ImporteRectificacion']['CuotaRectificada']}</sf:CuotaRectificada>
                        </sf:ImporteRectificacion>
                    `;
                }
            }            
        }

        _xml += `<sf:DescripcionOperacion>${_XMLData['ticketdata']['DescripcionFactura']}</sf:DescripcionOperacion>`;

        if (_XMLData['ticketdata']['DestinatarioFactura']){
            if (['F2', 'R5'].includes(_XMLData['ticketdata']['TipoFactura']) == false){
                _xml += '<sf:Destinatarios>';
                _xml += `
                    <sf:IDDestinatario>
                        <sf:NombreRazon>${_XMLData['ticketdata']['NombreDestinatarioFactura']}</sf:NombreRazon>
                        <sf:NIF>${_XMLData['ticketdata']['IdDestinatarioFactura']}</sf:NIF>
                    </sf:IDDestinatario>

                `;
                _xml += '</sf:Destinatarios>';
            }
        }

        _xml += '<sf:Desglose>';
        for(let _detalleDesglose of _XMLData['tickettaxes']['DetalleDesglose']){
            _xml += `
                <sf:DetalleDesglose>
                    <sf:ClaveRegimen>${_detalleDesglose['ClaveRegimen']}</sf:ClaveRegimen>
                    <sf:CalificacionOperacion>${_detalleDesglose['CalificacionOperacion']}</sf:CalificacionOperacion>
                    <sf:TipoImpositivo>${_detalleDesglose['TipoImpositivo']}</sf:TipoImpositivo>
                    <sf:BaseImponibleOimporteNoSujeto>${_detalleDesglose['BaseImponible']}</sf:BaseImponibleOimporteNoSujeto>
                    <sf:CuotaRepercutida>${_detalleDesglose['CuotaRepercutida']}</sf:CuotaRepercutida>
                </sf:DetalleDesglose>
            `;
        }
        _xml += '</sf:Desglose>';

        _xml += `<sf:CuotaTotal>${_XMLData['ticketdata']['CuotaTotal']}</sf:CuotaTotal>`;
        _xml += `<sf:ImporteTotal>${_XMLData['ticketdata']['ImporteTotal']}</sf:ImporteTotal>`;

        _xml += '<sf:Encadenamiento>';
        if (_XMLData['previoussign']){
            _xml += `
                <sf:RegistroAnterior>
                <sf:IDEmisorFactura>${_XMLData['previoussign']['IDEmisorFactura']}</sf:IDEmisorFactura>
                <sf:NumSerieFactura>${_XMLData['previoussign']['NumSerieFactura']}</sf:NumSerieFactura>
                <sf:FechaExpedicionFactura>${_XMLData['previoussign']['FechaExpedicionFactura']}</sf:FechaExpedicionFactura>
                <sf:Huella>${_XMLData['previoussign']['Huella']}</sf:Huella>
                </sf:RegistroAnterior>
            `;
        }
        else {
            _xml += '<sf:PrimerRegistro>S</sf:PrimerRegistro>';
        }
        _xml += '</sf:Encadenamiento>';

        _xml += `
            <sf:SistemaInformatico>
                <sf:NombreRazon>${_XMLData['softwaredata']['NombreRazon']}</sf:NombreRazon>
                <sf:NIF>${_XMLData['softwaredata']['NIF']}</sf:NIF>
                <sf:NombreSistemaInformatico>${_XMLData['softwaredata']['NombreSistemaInformatico']}</sf:NombreSistemaInformatico>
                <sf:IdSistemaInformatico>${_XMLData['softwaredata']['IdSistemaInformatico']}</sf:IdSistemaInformatico>
                <sf:Version>${_XMLData['softwaredata']['Version']}</sf:Version>
                <sf:NumeroInstalacion>${_XMLData['softwaredata']['NumeroInstalacion']}</sf:NumeroInstalacion>
                <sf:TipoUsoPosibleSoloVerifactu>${_XMLData['softwaredata']['TipoUsoPosibleSoloVerifactu']}</sf:TipoUsoPosibleSoloVerifactu>
                <sf:TipoUsoPosibleMultiOT>${_XMLData['softwaredata']['TipoUsoPosibleMultiOT']}</sf:TipoUsoPosibleMultiOT>
                <sf:IndicadorMultiplesOT>${_XMLData['softwaredata']['IndicadorMultiplesOT']}</sf:IndicadorMultiplesOT>
            </sf:SistemaInformatico>
        `;

        _xml += `<sf:FechaHoraHusoGenRegistro>${_XMLData['ticketdata']['FechaGenRegistro']}</sf:FechaHoraHusoGenRegistro>`;
        _xml += `<sf:TipoHuella>${_XMLData['ticketdata']['TipoHuella']}</sf:TipoHuella>`;
        _xml += `<sf:Huella>${_XMLData['ticketdata']['HuellaFactura']}</sf:Huella>`;

        _xml += '</sf:RegistroAlta>';
        _xml += '</sfLR:RegistroFactura>';

        return _xml;
    }
}
